import { t } from '@lingui/core/macro';

import dataLayer from '@rover/globals/dataLayer';
import $ from '@rover/globals/jquery';
import Mustache from '@rover/globals/Mustache';
import Rover from '@rover/globals/Rover';
import _ from '@rover/globals/underscore';
import { captureError } from '@rover/rsdk/src/modules/ErrorReporting';
import { getCachedSSPCoupon } from '@rover/shared/js/utils/sspCouponCache';
import Alerts from '@rover/utilities/Alerts';
import inject from '@rover/utilities/injector';
import { localStorage } from '@rover/utilities/storage';

$(() => {
  if (!$('.js-registration').length) {
    return;
  }

  // Redirect through the SSP sitter page after signup so Django can trigger coupon redemption.
  const $nextInput = $('.js-sign-up-form input[name="next"]');
  if ($nextInput.length) {
    const currentNext = $nextInput.val() || '';
    const cachedCode = getCachedSSPCoupon();
    if (cachedCode) {
      if (!currentNext.includes('/sit/')) {
        const sitUrl = currentNext
          ? `/sit/${cachedCode}/?next=${encodeURIComponent(currentNext)}`
          : `/sit/${cachedCode}/`;
        $nextInput.val(sitUrl);
        dataLayer.push({
          event: 'ssp-coupon-cache-applied',
          sspCouponCode: cachedCode,
          originalNext: currentNext,
        });
      } else {
        dataLayer.push({
          event: 'ssp-coupon-cache-skipped',
          sspCouponCode: cachedCode,
          currentNext,
        });
      }
    }
  }

  // Impact attribution
  // Migrated to SSR /account/continue: https://roverdotcom.atlassian.net/browse/DEV-153008
  // in a slight timeout in case this is the frist page the user visits.
  // (a script in GTM captures the click id, so let's give it a second or two).
  const countryCodeSelector = '#country_code';
  window.setTimeout(() => {
    let clickId = null;
    if (localStorage) {
      clickId = localStorage.getItem('clickId');
    }

    if (clickId) {
      const attributionInputs = `
            <input type="hidden" name="external_attribution_id" value="${clickId}"/>
            <input type="hidden" name="external_attribution_source" value="impact"/>`;

      $('.js-sign-up-form').append(attributionInputs);
      $('.js-facebook-register').append(attributionInputs);
    }
  }, 2000);

  const handleFacebookSignInError = function () {
    Alerts.showToast({
      severity: 'danger',
      message: t`We were unable to sign you in with your Facebook account. Please sign in using your email address instead.`,
      container: '.js-facebook-alerts-container',
    });
  };

  const gdprExplicitFieldsBasedOnCountryCode = () => {
    // Allows us to skip the GDPR logic in pages without the country code config
    if ($('.js-registration-skip-gdpr').length) {
      return;
    }

    let countryCode = $(countryCodeSelector).val();
    if (!countryCode) {
      captureError(
        new Error(
          "Country code doesn't exist on the page or it's empty, using Rover.context.countryCode"
        ),
        {
          countryCode: Rover.context.countryCode,
        }
      );
      countryCode = inject('Rover.context.countryCode', 'US');
    }

    const signupCountryConfigurationRaw = $('#sign-up-country-configuration').val();
    if (!signupCountryConfigurationRaw) {
      captureError(
        new Error("#sign-up-country-configuration doesn't exist on the page or it's empty")
      );
      return;
    }
    const signupCountryConfiguration = JSON.parse(signupCountryConfigurationRaw);
    if (!_.has(signupCountryConfiguration, countryCode)) {
      captureError(new Error("signupCountryConfiguration doesn't have the country code"), {
        countryCode,
        signupCountryConfiguration,
      });
      return;
    }
    const countryConfiguration = signupCountryConfiguration[countryCode];

    if (_.has(countryConfiguration.signupCheckboxes, 'tos')) {
      $('.js-sign-up-form .js-tos').removeClass('hidden');
      const { tos } = countryConfiguration.signupCheckboxes;
      $('.js-sign-up-form .js-tos label').html(`<p>${tos.text}</p>`);
      $('.js-sign-up-form .js-tos input').prop('checked', tos.checked);
    } else {
      $('.js-sign-up-form .js-tos').addClass('hidden');
    }

    if (_.has(countryConfiguration.signupCheckboxes, 'marketingCommsOptIn')) {
      $('.js-sign-up-form .js-opt-in').removeClass('hidden');
      const { marketingCommsOptIn } = countryConfiguration.signupCheckboxes;
      $('.js-sign-up-form .js-opt-in label').html(`<p>${marketingCommsOptIn.text}</p>`);
      $('.js-sign-up-form .js-opt-in input').prop('checked', marketingCommsOptIn.checked);
    } else {
      $('.js-sign-up-form .js-opt-in').addClass('hidden');
    }

    const isOptOutVariant = _.has(countryConfiguration.signupCheckboxes, 'marketingCommsOptOut');
    const $optOut = $('.js-sign-up-form .js-opt-out');
    const $optOutInput = $optOut.find('.js-email-opt-out');

    if (isOptOutVariant) {
      const { marketingCommsOptOut } = countryConfiguration.signupCheckboxes;
      $optOut.removeClass('hidden');
      $optOut.find('.js-opt-out-heading').html(marketingCommsOptOut.heading);
      $optOut.find('label').html(`<p>${marketingCommsOptOut.text}</p>`);
      // Never clobber a refusal the user already expressed: the box comes back checked from
      // the submitted data after a server-side validation error, and a country switch must
      // not discard a live tick either. The config default only fills the gap.
      $optOutInput.prop('checked', $optOutInput.prop('checked') || marketingCommsOptOut.checked);
      // Terms are folded into the Sign Up CTA copy, so submit acceptance implicitly.
      $('.js-sign-up-form #agree_to_tos').prop('checked', true);
      // #email_opt_in is the hidden carrier of marketing consent: subscribed unless opted out.
      $('.js-sign-up-form #email_opt_in').prop('checked', !$optOutInput.prop('checked'));
      $optOutInput.off('change.optOut').on('change.optOut', function toggleEmailOptIn() {
        $('.js-sign-up-form #email_opt_in').prop('checked', !$(this).prop('checked'));
      });
    } else {
      // Hiding the card is not enough: the input still submits, so a tick made before the
      // switch would send an opt-out for a country that never offered one.
      $optOut.addClass('hidden');
      $optOutInput.off('change.optOut').prop('checked', false);
      // Only reset the consent carrier when the new country doesn't manage it itself,
      // otherwise this would clobber the value the opt-in branch just set.
      if (!_.has(countryConfiguration.signupCheckboxes, 'marketingCommsOptIn')) {
        $('.js-sign-up-form #email_opt_in').prop('checked', false);
      }
      // Same for the implicit Terms acceptance the variant branch forces: a country with no
      // Terms checkbox of its own would otherwise inherit an accepted one it never showed.
      if (!_.has(countryConfiguration.signupCheckboxes, 'tos')) {
        $('.js-sign-up-form #agree_to_tos').prop('checked', false);
      }
    }

    const { signUpConfirmText } = countryConfiguration;
    // The variant folds Terms into this text and left-aligns it; the server picks the class on
    // first render, so a country switch has to swap it too.
    $('.js-sign-up-form .js-sign-up-confirm .js-sign-up-confirm-text')
      .toggleClass('opt-out-confirm-text', isOptOutVariant)
      .toggleClass('small-text text-center', !isOptOutVariant)
      .html(`<p>${signUpConfirmText}</p>`);
  };

  const validCountryCodes = new Set(
    Array.from(document.querySelectorAll('#sign-up-country-selector option[value]')).map(
      (opt) => opt.value.split(',')[0]
    )
  );

  const selectCountryCode = function (e) {
    const values = e.target.value.split(',');
    const countryCode = values[0];
    const countryName = values[1];

    if (!validCountryCodes.has(countryCode)) return;

    $(countryCodeSelector).val(countryCode);
    const $locationLabel = $('.sign-up-location-label');
    const oldCountryName = $locationLabel.data('country-name');
    $locationLabel.text($locationLabel.text().replace(oldCountryName, countryName));
    $locationLabel.data('country-name', countryName);

    if ($('#zip_code').val()) $('#zip_code').val('');
    gdprExplicitFieldsBasedOnCountryCode();
    // This will change the selected text of the dropdown back to "Switch Country" but will keep selected the language
    const $select = document.querySelector('#sign-up-country-selector');
    $select.value = t`Switch Country`;
  };

  const facebookSignIn = function () {
    Rover.facebook.signup(false, handleFacebookSignInError);
  };

  const handleFacebookSignUpError = function () {
    dataLayer.push({
      event: 'account-sign-up-facebook-sign-up',
      submitStatus: 'fail',
      errorMessage: t`We were unable to connect with Facebook. Please sign up using your email address instead.`,
    });
    Alerts.showToast({
      severity: 'danger',
      message: t`We were unable to connect with Facebook. Please sign up using your email address instead.`,
      container: '.js-facebook-alerts-container',
    });
  };

  const facebookSignUp = function () {
    Rover.facebook.signup(false, handleFacebookSignUpError);
  };

  const fireAnalyticsForToggle = function (e, data) {
    if (data.on !== '.js-sign-in' && data.on !== '.js-sign-up') return;

    const eventName = data.on.replace('.js-', '');
    switch (eventName) {
      case 'sign-up':
        dataLayer.push({
          event: 'account-sign-in-click-to-sign-up',
        });
        break;
      case 'sign-in':
        dataLayer.push({
          event: 'account-sign-up-click-to-sign-in',
        });
        break;
      default:
        break;
    }
  };

  const fireAnalyticsForSubmit = function (e) {
    if ($(e.currentTarget).hasClass('js-sign-up-form')) {
      dataLayer.push({ event: 'sign-up-button-clicked' });
    } else {
      dataLayer.push({
        event: 'account-sign-up-click-to-sign-in',
      });
    }
  };

  const fireAnalyticsForClick = function (type, action) {
    return function () {
      if (type === 'google' || type === 'apple') {
        if (action === 'sign-in' || action === 'sign-up') {
          dataLayer.push({ event: `account-${action}-${type}` });
        }
      }
    };
  };

  const initializeSignInValidation = () => {
    $('.js-sign-in-form').roverValidate();
  };
  const enableSignUpButtonAfterFormChanges = () => {
    $('.sign-up-form')
      .find('.form-control')
      .on('change', () => {
        $('.sign-up-form')
          .find(':submit')
          .not('.js-prevent-validate-disabled')
          .prop('disabled', false);
      });
  };
  const initializeSignUpValidation = () => {
    enableSignUpButtonAfterFormChanges();
    $('.js-sign-up-form').roverValidate({
      focusInvalid: false,
      // if focusInvalid is true, the form auto-scrolls to previous invalid input on blur, which is annoying.
      // This makes it focus the first invalid element only on submit (See https://stackoverflow.com/a/10112391/1094910)
      invalidHandler: (form, validator) => {
        if (validator.numberOfInvalids() > 0) validator.errorList[0].element.focus();
      },
      rules: {
        first_name: {
          required: true,
          nameValidate: { name: $('#first_name').val() },
        },
        last_name: {
          required: true,
          nameValidate: { name: $('#last_name').val() },
        },
        email: {
          required: true,
          email: true,
          remote: {
            url: Rover.urls.validateEmail,
            type: 'GET',
          },
        },
        zip_code: {
          required: true,
          zippostalValidate: {
            countrySelector: $('#country_code'),
          },
          remote: {
            url: Rover.urls.validateZipcode,
            type: 'GET',
            data: {
              remote: true,
              cache: false,
              country_code() {
                return $(countryCodeSelector).val();
              },
            },
          },
        },
        password: {
          required: true,
          remote: {
            url: Rover.urls.validatePassword,
            type: 'POST',
            data: {
              remote: true,
              cache: false,
              first_name() {
                return $('#first_name').val();
              },
              last_name() {
                return $('#last_name').val();
              },
              email() {
                return $('#signup-email').val();
              },
            },
          },
        },
        agree_to_tos: {
          required: true,
        },
      },
      messages: {
        first_name: {
          nameValidate: t`First name must contain at least one letter.`,
        },
        last_name: {
          nameValidate: t`Last name must contain at least one letter.`,
        },
        agree_to_tos: {
          required: t`Please agree to our Terms of Service and Privacy Statement.`,
        },
      },
    });
  };

  const referralSourceDetail = function () {
    const isOther = $(this).val() === 'other';
    const $other = $("[name='referral_source_detail']");

    if (isOther) {
      $other.removeClass('hide');
      $other.focus();
    } else {
      $other.addClass('hide');
    }
  };

  const initMailcheck = function () {
    const templateMessage = t({ id: `Did you mean` });
    const $input = $('.js-signup-email');
    const $formGroup = $input.closest('.form-group');
    const template = `<p class="help-text text-info"><i class="rover-icon rover-icon-in-exclamation-triangle"></i> ${templateMessage} {{ address }}@<strong>{{ domain }}</strong>?</p>`;

    const clearSuggestion = function () {
      $formGroup.find('.help-text').remove();
    };

    const addSuggestion = function (suggestionObj) {
      $formGroup.append(Mustache.render(template, suggestionObj));
    };

    $input.on('blur', () => {
      $input.mailcheck({
        suggested(el, suggestion) {
          clearSuggestion();
          addSuggestion(suggestion);
        },
        empty: clearSuggestion,
      });
    });
  };

  const addBindings = () => {
    $('.js-sign-up-form .agree-to-tos').on('change', () => {
      const $agreeTOS = $('.js-sign-up-form .agree-to-tos');
      const $agreeTOSValidation = $('.js-sign-up-form .agree-to-tos-validation');
      $agreeTOSValidation.prop('checked', $agreeTOS.prop('checked'));
    });
    $('.js-email-sign-up').on('click', initializeSignUpValidation);
    $('.js-facebook-sign-up').on('click', facebookSignUp);
    $('.js-facebook-sign-in').on('click', facebookSignIn);
    $('.js-google-sign-up').on('click', fireAnalyticsForClick('google', 'sign-up'));
    $('.js-google-sign-in').on('click', fireAnalyticsForClick('google', 'sign-in'));
    $('.js-apple-sign-up').on('click', fireAnalyticsForClick('apple', 'sign-up'));
    $('.js-apple-sign-in').on('click', fireAnalyticsForClick('apple', 'sign-in'));
    $('.js-sign-up-form, .js-sign-in-form').on('submit', fireAnalyticsForSubmit);
    $('[name="referral_source"]').on('change', referralSourceDetail);
    $(window).on('toggle-fired', fireAnalyticsForToggle);

    $('.sign-up-country-selector').on('change', selectCountryCode);
  };

  const setFieldsFromIntl = () => {
    if (window.Intl) {
      const resolvedOptions = Intl.DateTimeFormat().resolvedOptions();
      const localeInputs = document.getElementsByClassName('js-intl-locale');
      for (let i = 0; i < localeInputs.length; i += 1) {
        localeInputs[i].value = resolvedOptions.locale;
      }
      const timeZoneInputs = document.getElementsByClassName('js-intl-time-zone');
      for (let i = 0; i < timeZoneInputs.length; i += 1) {
        timeZoneInputs[i].value = resolvedOptions.timeZone;
      }
    }
  };

  const init = () => {
    addBindings();
    initializeSignInValidation();
    initMailcheck();
    setFieldsFromIntl();
    gdprExplicitFieldsBasedOnCountryCode();
  };

  init();
});
